import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import type { Account, AccountProviderInfo, UserSettings } from '@/api/auth';
import {
  CASINO_CAROUSEL_SIZE,
  CASINO_PAGE_SIZE_DESKTOP,
  WALLET_CORE_SESSION_PROVIDERS,
  casinoNavSlug,
  flagTypeFromSlug,
  isCasinoFlagSlug,
  walletSessionProvider,
  type CasinoCategory,
  type CasinoGameFilters,
  type CasinoGamesPage,
  type CasinoListOptions,
  type BiggestCasinoWinning,
  type LatestCasinoTransaction,
  type CasinoVendor,
  type GameSessionRequest,
  type GameSessionResponse,
} from '@/domain/casino';
import { getAccount, getAccountId, getUserSettings } from './auth';
import { api } from './client';
import { endpoints } from './endpoints';
import { queryKeys } from './query-keys';

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return '';
}

function normalizeGame(raw: unknown): CasinoGamesPage['Data'][number] | null {
  const row = asRecord(raw);
  if (!row) return null;
  const name = toString(row.Name).trim();
  const gameTag = toString(row.GameTag).trim();
  const vendorName = toString(row.VendorName).trim();
  const providerName = toString(row.ProviderName).trim();
  const providerGameId = toNumber(row.ProviderGameId);
  if (!name || !gameTag || !vendorName || !providerName || providerGameId == null) return null;
  return {
    Id: toNumber(row.Id),
    VendorId: toNumber(row.VendorId),
    VendorName: vendorName,
    CategoryId: toNumber(row.CategoryId),
    CategoryName: toString(row.CategoryName).trim() || 'slots',
    ExternalId: toString(row.ExternalId) || null,
    Name: name,
    GameTag: gameTag,
    GameCategoryTag: toString(row.GameCategoryTag) || null,
    ProviderId: toNumber(row.ProviderId),
    ProviderName: providerName,
    Popular: Boolean(row.Popular),
    FavoriteId: toNumber(row.FavoriteId),
    ProviderGameId: providerGameId,
    Position: toNumber(row.Position),
  };
}

function normalizeGamesPage(raw: unknown): CasinoGamesPage {
  const body = asRecord(raw);
  const dataRaw = body?.Data;
  const data = Array.isArray(dataRaw)
    ? dataRaw.map(normalizeGame).filter((g): g is NonNullable<typeof g> => g != null)
    : [];
  const filter = asRecord(body?.Filter);
  return {
    Data: data,
    Filter: filter
      ? {
          PageNumber: toNumber(filter.PageNumber) ?? 1,
          NumberOfResultsToDisplay: toNumber(filter.NumberOfResultsToDisplay) ?? undefined,
          NumberOfPages: toNumber(filter.NumberOfPages) ?? undefined,
          NumberOfResults: toNumber(filter.NumberOfResults) ?? undefined,
          TotalNumberOfResults: toNumber(filter.TotalNumberOfResults) ?? undefined,
        }
      : null,
  };
}

function normalizeVendor(raw: unknown): CasinoVendor | null {
  const row = asRecord(raw);
  if (!row) return null;
  const id = toNumber(row.Id);
  const name = toString(row.Name).trim();
  if (id == null || !name) return null;
  return {
    Id: id,
    Name: name,
    ProviderId: toNumber(row.ProviderId),
    ProviderName: toString(row.ProviderName) || null,
    Position: toNumber(row.Position),
  };
}

function normalizeCategory(raw: unknown): CasinoCategory | null {
  const row = asRecord(raw);
  if (!row) return null;
  const id = toNumber(row.CategoryId);
  const name = toString(row.CategoryName).trim();
  if (id == null || !name) return null;
  return { CategoryId: id, CategoryName: name };
}

/** Account / player context for catalogue filters. */
export function casinoFilterContext(
  account: Account | null | undefined = getAccount(),
  user: UserSettings | null | undefined = getUserSettings(),
): Pick<CasinoGameFilters, 'AccountGuid' | 'MachineGuid' | 'CurrencyId' | 'CountryId'> {
  const accountGuid = account?.Guid ?? '';
  const loggedIn = Boolean(user?.Guid);

  // Catalogue list endpoints expect CurrencyId 0 (legacy home rails); player
  // currency is only relevant for wallet/session launches, not list filters.
  if (loggedIn && user) {
    return {
      AccountGuid: accountGuid,
      MachineGuid: user.Guid ?? null,
      CurrencyId: 0,
      CountryId: typeof user.CountryId === 'number' ? user.CountryId : null,
    };
  }

  // Guests can browse lobbies; launch/play still requires login in CasinoPlayPage.
  return {
    AccountGuid: accountGuid,
    MachineGuid: null,
    CurrencyId: 0,
    CountryId: accountCountriesFirstId(account),
  };
}

function accountCountriesFirstId(account: Account | null | undefined): number | null {
  const countries = account?.Countries;
  if (!Array.isArray(countries) || countries.length === 0) return null;
  const first = countries.find((c) => c && c.id && c.text !== 'Default');
  if (!first) return null;
  const n = Number(first.id);
  return Number.isFinite(n) ? n : null;
}

/** Lobby / catalogue browse is always allowed; only play is login-gated. */
export function canBrowseCasino(
  _account: Account | null | undefined = getAccount(),
  _user: UserSettings | null | undefined = getUserSettings(),
): boolean {
  return true;
}

export function listOptions(
  filters: CasinoGameFilters,
  pageNumber: number,
  pageSize: number,
): CasinoListOptions {
  return {
    Paging: {
      PageNumber: pageNumber,
      NumberOfResultsToDisplay: pageSize,
    },
    Sorting: { SortBy: 'Position', Direction: 'asc' },
    TableFilters: { ...filters, CurrencyId: 0 },
  };
}

export async function fetchCasinoVendors(
  filters: CasinoGameFilters,
  signal?: AbortSignal,
): Promise<CasinoVendor[]> {
  const body = listOptions(filters, 1, 500);
  const raw = await api.post<unknown>(
    endpoints.casino.vendors.host,
    endpoints.casino.vendors.path,
    body as unknown as Record<string, unknown>,
    signal,
  );
  const list = Array.isArray(raw) ? raw : asRecord(raw)?.Data;
  if (!Array.isArray(list)) return [];
  return list.map(normalizeVendor).filter((v): v is CasinoVendor => v != null);
}

export async function fetchCasinoGames(
  filters: CasinoGameFilters,
  pageNumber: number,
  pageSize: number,
  signal?: AbortSignal,
): Promise<CasinoGamesPage> {
  const body = listOptions(filters, pageNumber, pageSize);
  const raw = await api.post<unknown>(
    endpoints.casino.games.host,
    endpoints.casino.games.path,
    body as unknown as Record<string, unknown>,
    signal,
  );
  return normalizeGamesPage(raw);
}

export async function fetchCasinoCategories(
  filters: Pick<CasinoGameFilters, 'AccountGuid' | 'MachineGuid'>,
  signal?: AbortSignal,
): Promise<CasinoCategory[]> {
  const body = {
    TableFilters: {
      AccountGuid: filters.AccountGuid,
      MachineGuid: filters.MachineGuid ?? null,
      CurrencyId: 0,
    },
  };
  const raw = await api.post<unknown>(
    endpoints.casino.categories.host,
    endpoints.casino.categories.path,
    body,
    signal,
  );
  const list = Array.isArray(raw) ? raw : asRecord(raw)?.Data;
  if (!Array.isArray(list)) return [];
  return list.map(normalizeCategory).filter((c): c is CasinoCategory => c != null);
}

export async function addCasinoFavorite(
  machineGuid: string,
  providerGameId: number,
  signal?: AbortSignal,
): Promise<{ Success?: boolean; Id?: number | null }> {
  return api.post(
    endpoints.casino.addFavorite.host,
    endpoints.casino.addFavorite.path,
    { MachineGuid: machineGuid, ProviderGameId: providerGameId },
    signal,
  );
}

export async function removeCasinoFavorite(
  favoriteId: number,
  signal?: AbortSignal,
): Promise<{ Success?: boolean }> {
  return api.post(
    endpoints.casino.removeFavorite.host,
    endpoints.casino.removeFavorite.path,
    { Id: favoriteId },
    signal,
  );
}

export async function createGameSession(
  providerNavSlug: string,
  request: GameSessionRequest,
  signal?: AbortSignal,
): Promise<GameSessionResponse> {
  const sessionSlug = walletSessionProvider(providerNavSlug);
  const useCore = WALLET_CORE_SESSION_PROVIDERS.has(sessionSlug);
  const endpoint = useCore
    ? endpoints.walletcore.gameSession(sessionSlug)
    : endpoints.wallet.gameSession(sessionSlug);

  return api.post<GameSessionResponse>(
    endpoint.host,
    endpoint.path,
    request,
    signal,
    useCore ? { encoding: 'json' } : undefined,
  );
}

export interface LaunchGameInput {
  provider: string;
  vendor: string;
  gameTag: string;
  category: string;
  isBonus?: boolean;
  isMobile?: boolean;
  returnUrl?: string;
  language?: string;
  account?: Account | null;
  user?: UserSettings | null;
}

/**
 * Create a playable URL for a game. Handles Wallet `GameUrl` responses and
 * SmartSoft token + LaunchUrl composition. Altira/RiseUp LocalApi paths are
 * not available in React — those fall through to wallet session when present.
 *
 * Default session body matches legacy slotsapi-style launches:
 * `WalletId`, `Game`, `Vendor`, `Lang`, `Device` (`desktop`|`mobile`),
 * `Account` (`SId-{SId}`), `ReturnUrl`, `WebSiteUrl`, `Currency`, `Percent`.
 * Country is not sent.
 */
export async function launchCasinoGame(input: LaunchGameInput): Promise<string> {
  const account = input.account ?? getAccount();
  const user = input.user ?? getUserSettings();
  const walletId = user?.WalletId?.trim();
  if (!walletId) throw new Error('WalletId is required to launch a game');

  const provider = casinoNavSlug(input.provider);
  const vendor = casinoNavSlug(input.vendor);
  const language = (input.language ?? user?.Language ?? account?.Language?.Short ?? 'en').trim();
  const returnUrl =
    input.returnUrl ??
    `${window.location.origin}/casino/${casinoNavSlug(input.category) || 'slots'}/${vendor}`;
  const currency = user?.CurrCode ?? user?.Currency ?? user?.CurrencyCode ?? null;
  const sid = user?.SId != null && String(user.SId).trim() !== '' ? String(user.SId).trim() : null;

  let request: GameSessionRequest;

  const bonusFields = input.isBonus === true ? { IsBonus: true as const } : {};

  if (provider === 'smartsoftgaming') {
    // Legacy OpenSmartSoftGame — token only, then compose LaunchUrl.
    request = {
      WalletId: walletId,
      Game: input.gameTag.charAt(0).toUpperCase() + input.gameTag.slice(1),
      Vendor: vendor,
      ...bonusFields,
    };
  } else if (provider === 'amatic') {
    // Legacy OpenGApiGame.
    request = {
      WalletId: walletId,
      AdminId: user?.HallId ?? undefined,
      Game: input.gameTag,
      Vendor: vendor,
      Mobile: input.isMobile === true,
      Language: language,
      ReturnUrl: returnUrl,
      ...bonusFields,
    };
  } else {
    // Legacy slotsapi-style body (also used for pragmatic / kosmos / cgames / …):
    // WalletId&Game&Vendor&Lang&Device=desktop|mobile&Account=SId-*&ReturnUrl&WebSiteUrl&Currency&Percent=90
    // Country is not sent.
    request = {
      WalletId: walletId,
      Game: input.gameTag,
      Vendor: vendor,
      Lang: language,
      Device: input.isMobile ? 'mobile' : 'desktop',
      ...(sid ? { Account: `SId-${sid}` } : {}),
      ReturnUrl: returnUrl,
      WebSiteUrl: window.location.host,
      ...(currency ? { Currency: currency } : {}),
      Percent: '90',
      ...bonusFields,
    };
  }

  const session = await createGameSession(provider, request);

  if (provider === 'smartsoftgaming' && session.Success && session.Token) {
    const info = findProviderInfo(account?.AccountProvidersInfo, 'SmartSoft Gaming');
    if (!info?.LaunchUrl) throw new Error(session.Error || 'SmartSoft launch URL missing');
    const category =
      input.category.charAt(0).toUpperCase() + input.category.slice(1);
    const gameName = request.Game;
    const mobileCategory = input.isMobile ? `${category}Mobile` : category;
    const portalName = info.ApiId ?? '';
    return `${info.LaunchUrl}/GameLauncher/Loader.aspx?GameCategory=${encodeURIComponent(mobileCategory)}&GameName=${encodeURIComponent(String(gameName))}&Token=${encodeURIComponent(session.Token)}&PortalName=${encodeURIComponent(portalName)}&ReturnUrl=${encodeURIComponent(returnUrl)}&Lang=${encodeURIComponent(language)}`;
  }

  if (provider === 'altira' && session.Success && session.Token) {
    const info = findProviderInfo(account?.AccountProvidersInfo, 'Altira');
    if (info?.LaunchUrl) {
      const agentId = session.AgentId ?? '';
      const username = `${user?.HallId ?? ''}_${user?.Username ?? ''}`;
      return `${info.LaunchUrl}/?AgentID=${encodeURIComponent(String(agentId))}&Username=${encodeURIComponent(username)}&GameID=${encodeURIComponent(input.gameTag)}&Token=${encodeURIComponent(session.Token)}&lang=${encodeURIComponent(language)}&cashier=${encodeURIComponent(returnUrl)}`;
    }
  }

  const gameUrl = session.GameUrl?.trim() || (session as { Url?: string }).Url?.trim();
  if (session.Success !== false && gameUrl) return gameUrl;

  throw new Error(session.Error?.trim() || 'Unable to start game session');
}

function findProviderInfo(
  list: AccountProviderInfo[] | null | undefined,
  name: string,
): AccountProviderInfo | null {
  if (!Array.isArray(list)) return null;
  const needle = name.toLowerCase();
  return list.find((p) => (p.Name ?? '').toLowerCase() === needle) ?? null;
}

function normalizeLatestWin(raw: unknown): LatestCasinoTransaction | null {
  const row = asRecord(raw);
  if (!row) return null;
  const providerId = toNumber(row.ProviderId);
  const vendorId = toNumber(row.VendorId);
  const gameTag = toString(row.GameTag).trim();
  const game = toString(row.Game).trim();
  const amount = toNumber(row.Amount);
  if (providerId == null || vendorId == null || !gameTag || !game || amount == null) return null;
  const timestampRaw = row.Timestamp;
  const timestamp =
    typeof timestampRaw === 'string'
      ? timestampRaw
      : timestampRaw instanceof Date
        ? timestampRaw.toISOString()
        : toString(timestampRaw);
  return {
    ProviderId: providerId,
    VendorId: vendorId,
    GameTag: gameTag,
    Game: game,
    Amount: amount,
    Currency: toString(row.Currency).trim(),
    Timestamp: timestamp,
  };
}

export async function fetchLatestCasinoWins(
  accountGuid: string,
  count = 200,
  signal?: AbortSignal,
): Promise<LatestCasinoTransaction[]> {
  const raw = await api.post<unknown>(
    endpoints.tickers.latestCasinoTransactions.host,
    endpoints.tickers.latestCasinoTransactions.path,
    { AccountGuid: accountGuid, Count: count },
    signal,
  );
  const list = Array.isArray(raw) ? raw : asRecord(raw)?.Data;
  if (!Array.isArray(list)) return [];
  return list
    .map(normalizeLatestWin)
    .filter((row): row is LatestCasinoTransaction => row != null);
}

function normalizeBiggestWin(raw: unknown): BiggestCasinoWinning | null {
  const row = asRecord(raw);
  if (!row) return null;
  const providerId = toNumber(row.ProviderId);
  const vendorId = toNumber(row.VendorId);
  const gameTag = toString(row.GameTag).trim();
  const game = toString(row.Game).trim();
  const amount = toNumber(row.Amount);
  if (providerId == null || vendorId == null || !gameTag || !game || amount == null) return null;
  const timestampRaw = row.Timestamp;
  const timestamp =
    typeof timestampRaw === 'string'
      ? timestampRaw
      : timestampRaw instanceof Date
        ? timestampRaw.toISOString()
        : toString(timestampRaw);
  return {
    ProviderId: providerId,
    VendorId: vendorId,
    GameTag: gameTag,
    Game: game,
    Amount: amount,
    Currency: toString(row.Currency).trim(),
    Timestamp: timestamp,
  };
}

export async function fetchBiggestCasinoWins(
  accountGuid: string,
  count = 30,
  signal?: AbortSignal,
): Promise<BiggestCasinoWinning[]> {
  const raw = await api.post<unknown>(
    endpoints.tickers.biggestCasinoWins.host,
    endpoints.tickers.biggestCasinoWins.path,
    { AccountGuid: accountGuid, Count: count },
    signal,
  );
  const list = Array.isArray(raw) ? raw : asRecord(raw)?.Data;
  if (!Array.isArray(list)) return [];
  return list.map(normalizeBiggestWin).filter((row): row is BiggestCasinoWinning => row != null);
}

/** Build TableFilters for a lobby/category view from route params. */
export function buildCasinoGameFilters(options: {
  account?: Account | null;
  user?: UserSettings | null;
  category?: string | null;
  vendorOrFlag?: string | null;
  searchText?: string | null;
  liveMode?: boolean;
  popular?: boolean | null;
  favorites?: boolean | null;
  flagType?: string | number | null;
  casino?: boolean | null;
}): CasinoGameFilters {
  const ctx = casinoFilterContext(options.account, options.user);
  const categorySlug = options.category ? casinoNavSlug(options.category) : '';
  const filter: CasinoGameFilters = {
    ...ctx,
    SearchText:
      options.searchText && options.searchText.trim().length > 2
        ? options.searchText.trim()
        : null,
  };

  if (options.favorites) {
    filter.FavoriteGames = true;
    filter.Casino = options.liveMode ? null : true;
    filter.LiveCasino = options.liveMode ? true : null;
    return filter;
  }

  if (options.liveMode || categorySlug === 'livecasino') {
    filter.LiveCasino = true;
    filter.Category = 'livecasino';
  } else {
    filter.Casino = options.casino !== false;
    if (categorySlug && categorySlug !== 'lobby' && categorySlug !== 'hot' && categorySlug !== 'favorites') {
      filter.Category = categorySlug;
    }
  }

  if (options.popular || categorySlug === 'hot') {
    filter.Popular = true;
    if (!filter.Category && categorySlug === 'hot') filter.Category = 'slots';
  }

  if (options.flagType != null) {
    filter.FlagType = options.flagType;
  }

  const segment = options.vendorOrFlag ? casinoNavSlug(options.vendorOrFlag) : '';
  if (segment) {
    if (segment === 'popular') {
      filter.Popular = true;
    } else if (isCasinoFlagSlug(segment)) {
      filter.FlagType = flagTypeFromSlug(segment);
    } else {
      // Vendor filter — API matches Vendor.Name; pass display-ish name from segment
      filter.Vendor = options.vendorOrFlag ?? segment;
    }
  }

  return filter;
}

export function casinoVendorsQuery(filters: CasinoGameFilters) {
  return queryOptions({
    queryKey: queryKeys.casino.vendors({ ...filters }),
    queryFn: ({ signal }) => fetchCasinoVendors(filters, signal),
    staleTime: 5 * 60_000,
    enabled: Boolean(filters.AccountGuid),
  });
}

export function casinoCategoriesQuery(
  filters: Pick<CasinoGameFilters, 'AccountGuid' | 'MachineGuid'>,
) {
  return queryOptions({
    queryKey: queryKeys.casino.categories({ ...filters }),
    queryFn: ({ signal }) => fetchCasinoCategories(filters, signal),
    staleTime: 5 * 60_000,
    enabled: Boolean(filters.AccountGuid),
  });
}

export function casinoGamesQuery(
  filters: CasinoGameFilters,
  pageSize = CASINO_CAROUSEL_SIZE,
) {
  return queryOptions({
    queryKey: queryKeys.casino.games({ ...filters, pageSize, page: 1 }),
    queryFn: ({ signal }) => fetchCasinoGames(filters, 1, pageSize, signal),
    staleTime: 30_000,
    enabled: Boolean(filters.AccountGuid),
  });
}

export function casinoGamesInfiniteQuery(
  filters: CasinoGameFilters,
  pageSize = CASINO_PAGE_SIZE_DESKTOP,
) {
  return infiniteQueryOptions({
    queryKey: queryKeys.casino.games({ ...filters, pageSize, infinite: true }),
    queryFn: ({ pageParam, signal }) => fetchCasinoGames(filters, pageParam, pageSize, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const page = lastPage.Filter?.PageNumber ?? 1;
      const pages = lastPage.Filter?.NumberOfPages;
      if (pages != null) return page < pages ? page + 1 : undefined;
      return lastPage.Data.length < pageSize ? undefined : page + 1;
    },
    staleTime: 30_000,
    enabled: Boolean(filters.AccountGuid),
  });
}

export function latestCasinoWinsQuery(accountGuid: string, count = 200) {
  return queryOptions({
    queryKey: queryKeys.tickers.latestWins(),
    queryFn: ({ signal }) => fetchLatestCasinoWins(accountGuid, count, signal),
    staleTime: 60_000,
    enabled: Boolean(accountGuid),
  });
}

export function biggestCasinoWinsQuery(accountGuid: string, count = 30) {
  return queryOptions({
    queryKey: queryKeys.tickers.biggestWins(),
    queryFn: ({ signal }) => fetchBiggestCasinoWins(accountGuid, count, signal),
    staleTime: 60_000,
    enabled: Boolean(accountGuid),
  });
}

/** Resolve account guid for queries (async bootstrap if needed). */
export async function ensureCasinoAccountGuid(): Promise<string> {
  return getAccountId();
}
